// useRoundsBoard — reads get_intl_rounds_board RPC for the Rounds product.
// Returns rounds (auto-detected from real persisted fixtures) and any
// moderation log entries that touched fixtures inside our universe.
// Honest by construction: no fabricated rounds, no fabricated predictions,
// no fabricated verdicts. agent_outputs are stripped server-side.

import { useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../../supabase/browser.ts";
import type { WcOdds } from "./useWcBoard.ts";

export interface RoundSignal {
  market: string; selection: string;
  fair_odds: number | null; market_odds: number | null;
  clv_gap: number | null; conviction_tier: string | null;
  desk_confidence: number | null;
  role?: string | null;        // 'primary' (desk call) | 'secondary' (conviction play)
  reasoning?: string | null;   // agent's one-line rationale for this market
}
export interface RoundFixture {
  fixture_id: string; home_team: string; away_team: string;
  home_code: string | null; away_code: string | null;
  kickoff_at: string; status: string;
  odds: WcOdds | null;
  signals: RoundSignal[];
}
export interface RoundBucket {
  tournament: string;
  round_code: string;
  window_start: string;   // ISO date
  window_end: string;
  fixture_count: number;
  fixtures: RoundFixture[];
}
export interface RoundsModerationLeg {
  leg_ref?: { fixture_id?: string; market?: string; selection?: string };
  fixture_id?: string;
  market?: string;
  selection?: string;
  reason_code?: string;
}
export interface RoundsModerationEntry {
  slip_id: string;
  product: string;
  decision: "APPROVE" | "TRIM_LEGS" | "REJECT_SLIP";
  ran_at: string;
  approved_count: number;
  rejected_count: number;
  approved_legs: RoundsModerationLeg[] | null;
  rejected_legs: RoundsModerationLeg[] | null;
}
export interface RoundsBoard {
  generated_at: string;
  rounds: RoundBucket[];
  moderation: RoundsModerationEntry[];
}

export function useRoundsBoard(): { board: RoundsBoard | null; loading: boolean; error: string | null } {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [board, setBoard] = useState<RoundsBoard | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (supabase.rpc as unknown as (name: string) => Promise<{ data: unknown; error: { message: string } | null }>)("get_intl_rounds_board")
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) { setError(rpcError.message); setBoard(null); }
        else { setBoard((data ?? null) as RoundsBoard | null); }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supabase]);

  return { board, loading, error };
}

/** Friendly label for the closed-enum round_code. */
export function roundLabel(code: string): string {
  switch (code) {
    case "WC-MD1": return "Group Stage · Matchday 1";
    case "WC-MD2": return "Group Stage · Matchday 2";
    case "WC-MD3": return "Group Stage · Matchday 3";
    case "WC-R32": return "Round of 32";
    case "WC-R16": return "Round of 16";
    case "WC-QF":  return "Quarter-finals";
    case "WC-SF":  return "Semi-finals";
    case "WC-3RD": return "Third-place playoff";
    case "WC-F":   return "Final";
    case "UCL":    return "Champions League fixture";
    case "UEL":    return "Europa League fixture";
    case "UECL":   return "Conference League fixture";
    case "EURO-Q": return "Euro Qualifier";
    case "EURO":   return "UEFA Euro fixture";
    default: return code;
  }
}
